import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { setPendingCalibrationCount, usePendingCalibrationCount } from '../stores/calibration-assignment.store';

import { useRefetchAfterOfflineSync } from './offline-sync.service';
import { GET_CALIBRATION_ASSIGNMENT, GET_CALIBRATION_ASSIGNMENT_SUMMARY, GET_MY_CALIBRATION_ASSIGNMENTS } from '../queries/calibration-assignment.queries';
import { ICalibrationAssignmentSummary } from '../types/calibration-assignment.types';

const PAGE_SIZE = 20;

// How often the staff session re-checks the pending count (badge freshness). Separate from
// the prompt cadence — the popup is throttled independently by the prompt scheduler cooldown.
const SUMMARY_POLL_MS = 2 * 60 * 1000;

// Owns the My Assignments list (cache-first lazy pagination — see CLAUDE.md
// "Paginated tables"): page reveal, "load more", and the post-offline-sync refresh.
export const useMyCalibrationAssignments = () => {
  // pagesLoaded resets to page 0 on every mount so a revisit re-pages from the top
  // instead of rendering stale pages a prior session accumulated in the cache.
  const [pagesLoaded, setPagesLoaded] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const {
    data, loading, error, called, fetchMore, refetch,
  } = useQuery(GET_MY_CALIBRATION_ASSIGNMENTS, {
    variables: { offset: 0, limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });

  const page = data && data.myCalibrationAssignments;
  const total: number = (page && page.total) || 0;

  // Show only pages revealed this session, capped by `total` so a deleted row past the
  // new total can't linger. Memoized off `page` so the prop reference is stable downstream.
  const assignments = useMemo(
    () => ((page && page.items) || ([] as ICalibrationAssignment[])).slice(0, Math.min(pagesLoaded * PAGE_SIZE, total)),
    [page, pagesLoaded, total],
  );

  useRefetchAfterOfflineSync(refetch, called, loading);

  const onLoadMore = useCallback(async () => {
    if (loadingMore || pagesLoaded * PAGE_SIZE >= total) return;
    const nextOffset = pagesLoaded * PAGE_SIZE;
    setPagesLoaded((p) => p + 1); // reveal the next page from cache immediately
    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      // Network half: the field policy offset-merges this page, correcting the
      // just-revealed cached rows in place when the response lands.
      await fetchMore({ variables: { offset: nextOffset, limit: PAGE_SIZE } });
    } catch (err) {
      console.error('useMyCalibrationAssignments load more failed:', err);
      setLoadMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, pagesLoaded, total, fetchMore]);

  return {
    assignments, total, loading, loadingMore, onLoadMore, fetchError: !!error, loadMoreError,
  };
};

// Loads a single owned assignment by id (scoring page / deep link) with the same
// post-offline-sync refresh. Exposes `refetch` so the page can refresh post-submit.
export const useCalibrationAssignment = (assignmentId: string) => {
  const {
    data, loading, error, called, refetch,
  } = useQuery(GET_CALIBRATION_ASSIGNMENT, {
    variables: { _id: assignmentId },
    fetchPolicy: 'cache-and-network',
  });

  useRefetchAfterOfflineSync(refetch, called, loading);

  const assignment: ICalibrationAssignment | undefined = (data && data.calibrationAssignment) || undefined;

  return { assignment, loading, fetchError: !!error, refetch };
};

interface IUseCalibrationAssignmentSummaryPollArgs {
  // Query gate: fetching and polling pause while false (offline, hidden tab, access loading).
  enabled: boolean;
  // True only on definitive access loss (signed out / role revoked) — the one state that
  // resets the badge; a paused poll holds the last-known count instead of reading as zero.
  accessRevoked: boolean;
  // Signed-in user id; a change zeroes the badge until the new user's poll lands (shared device).
  identityKey?: string;
}

// Lightweight pending-count poll for the staff session, driving the badge var (cache-and-network
// + minutes-scale poll). `enabled` gates everything, so polling pauses when it goes false.
export const useCalibrationAssignmentSummaryPoll = ({ enabled, accessRevoked, identityKey }: IUseCalibrationAssignmentSummaryPollArgs) => {
  const {
    data, startPolling, stopPolling, refetch, called, loading,
  } = useQuery(GET_CALIBRATION_ASSIGNMENT_SUMMARY, {
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Scoring a calibration assignment completes it server-side during offline sync, dropping the
  // pending count; refresh the badge on sync instead of waiting for the next poll tick.
  useRefetchAfterOfflineSync(refetch, called, loading, enabled);

  const summary: ICalibrationAssignmentSummary | undefined = (data && data.calibrationAssignmentSummary) || undefined;
  const heldCount = usePendingCalibrationCount();

  // Pre-paint badge mirror. Only fresh data or definitive access loss moves it — a skipped
  // query reads `data: undefined`, which must never masquerade as "zero pending".
  useLayoutEffect(() => {
    if (accessRevoked) {
      setPendingCalibrationCount(0);
      return;
    }
    if (summary) setPendingCalibrationCount(summary.pendingCount || 0);
  }, [accessRevoked, summary]);

  // A different signed-in user must not inherit the prior user's badge (clearStore leaves this
  // makeVar set). Reset on an identity *change* only — the mount would clobber the summary count.
  const prevIdentity = useRef(identityKey);
  useEffect(() => {
    if (prevIdentity.current === identityKey) return;
    prevIdentity.current = identityKey;
    setPendingCalibrationCount(0);
  }, [identityKey]);

  // Drive Apollo's own poll off `enabled`; stopPolling covers both the pause and unmount cleanup.
  useEffect(() => {
    if (enabled) startPolling(SUMMARY_POLL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [enabled, startPolling, stopPolling]);

  // Consumers read the held var-backed count, so a paused poll can't read as zero.
  return { pendingCount: heldCount };
};
